import React, { useState, useEffect } from 'react';
import {
  Typography, Container, Paper, Grid, CircularProgress, Box,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Accordion, AccordionSummary, AccordionDetails, Divider, Stack, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import { getFunctions, httpsCallable } from 'firebase/functions';

function ApiUsage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [usage, setUsage] = useState(null);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApiUsage = async () => {
      try {
        const functions = getFunctions();
        const getApiUsage = httpsCallable(functions, 'getApiUsage');
        const result = await getApiUsage();
        setUsage({
          requestsRemaining: result.data.requestsRemaining,
          requestsUsed: result.data.requestsUsed,
        });
        setLogs(result.data.logs || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchApiUsage();
  }, []);

  const formatTimestamp = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  const getFunctionLabel = (name) => {
    switch (name) {
      case 'updateVegasOdds': return 'Refresh Game Data';
      case 'fetchGameScores': return 'Fetch Scores';
      case 'moveGamesToWeek': return 'Move Games';
      default: return name;
    }
  };

  const functionChip = (log) => (
    <Chip
      label={getFunctionLabel(log.functionName)}
      size="small"
      variant="outlined"
      color={log.functionName === 'updateVegasOdds' ? 'primary' : 'secondary'}
    />
  );
  const triggerChip = (log) => (
    log.trigger === 'scheduled'
      ? <Chip icon={<ScheduleIcon />} label="Auto" size="small" color="info" variant="outlined" />
      : <Chip icon={<TouchAppIcon />} label="Manual" size="small" variant="outlined" />
  );
  const statusChip = (log) => (
    log.success ? (
      <Chip
        icon={<CheckCircleIcon />}
        label={log.errors?.length > 0 ? `OK (${log.errors.length} issues)` : 'Success'}
        size="small"
        color={log.errors?.length > 0 ? 'warning' : 'success'}
        variant="outlined"
      />
    ) : (
      <Chip icon={<ErrorIcon />} label="Failed" size="small" color="error" variant="outlined" />
    )
  );

  // Phones: one card per call, issues listed inline (they're short).
  const renderLogCards = () => (
    <Stack spacing={1.5} role="list" aria-label="API call history">
      {logs.map((log) => (
        <Paper
          key={log.id}
          role="listitem"
          data-testid="api-log-card"
          variant="outlined"
          sx={{ p: 1.5, backgroundColor: !log.success ? '#fff3f0' : (log.errors?.length > 0 ? '#fffbf0' : 'inherit') }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {functionChip(log)}
            {triggerChip(log)}
            <Box sx={{ flexGrow: 1 }} />
            {statusChip(log)}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {formatTimestamp(log.timestamp)}
            {log.week ? ` · Week ${log.week}` : ''}
            {log.apiQuota?.requestsRemaining ? ` · ${log.apiQuota.requestsRemaining} quota left` : ''}
          </Typography>
          {log.message && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>{log.message}</Typography>
          )}
          {log.errors?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {log.errors.map((err, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.5 }}>
                  <Chip label={err.type.replace(/_/g, ' ')} size="small" color="warning" sx={{ fontSize: '0.7rem' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{err.detail}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      ))}
    </Stack>
  );

  if (loading) {
    return (
      <Container maxWidth="md" style={{ marginTop: '20px' }}>
        <Grid container justifyContent="center">
          <CircularProgress />
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" style={{ marginTop: '20px' }}>
      {error && <Typography color="error" gutterBottom>{error}</Typography>}

      {/* API Quota Card */}
      <Paper style={{ padding: '20px', marginBottom: '20px' }}>
        <Typography variant="h5" gutterBottom>API Quota</Typography>
        {usage && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary">{usage.requestsRemaining}</Typography>
                <Typography variant="body2" color="textSecondary">Remaining</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box textAlign="center">
                <Typography variant="h4">{usage.requestsUsed}</Typography>
                <Typography variant="body2" color="textSecondary">Used</Typography>
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Recent API Calls Log */}
      <Paper style={{ padding: '20px' }}>
        <Typography variant="h5" gutterBottom>API Call History</Typography>

        {logs.length === 0 ? (
          <Typography color="textSecondary">No API calls logged yet.</Typography>
        ) : isMobile ? renderLogCards() : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Time</strong></TableCell>
                  <TableCell><strong>Function</strong></TableCell>
                  <TableCell><strong>Trigger</strong></TableCell>
                  <TableCell><strong>Week</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Quota Left</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <TableRow
                      sx={{
                        backgroundColor: !log.success ? '#fff3f0' : (log.errors?.length > 0 ? '#fffbf0' : 'inherit'),
                        '&:hover': { backgroundColor: '#f5f5f5' },
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell>{functionChip(log)}</TableCell>
                      <TableCell>{triggerChip(log)}</TableCell>
                      <TableCell>{log.week || '—'}</TableCell>
                      <TableCell>{statusChip(log)}</TableCell>
                      <TableCell>{log.apiQuota?.requestsRemaining || '—'}</TableCell>
                    </TableRow>

                    {/* Expandable error details row */}
                    {(log.errors?.length > 0 || log.message) && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ padding: 0, borderBottom: 'none' }}>
                          <Accordion elevation={0} disableGutters sx={{ backgroundColor: 'transparent' }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: '32px', '& .MuiAccordionSummary-content': { margin: '4px 0' } }}>
                              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                                {log.message}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ padding: '0 16px 12px' }}>
                              {log.errors?.length > 0 && (
                                <>
                                  <Divider sx={{ mb: 1 }} />
                                  <Typography variant="subtitle2" gutterBottom>Issues:</Typography>
                                  {log.errors.map((err, idx) => (
                                    <Box key={idx} sx={{ mb: 0.5, pl: 1 }}>
                                      <Chip
                                        label={err.type.replace(/_/g, ' ')}
                                        size="small"
                                        color="warning"
                                        sx={{ mr: 1, fontSize: '0.7rem' }}
                                      />
                                      <Typography variant="body2" component="span" sx={{ fontSize: '0.85rem' }}>
                                        {err.detail}
                                      </Typography>
                                    </Box>
                                  ))}
                                </>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
}

export default ApiUsage;
